import { router, usePage } from "@inertiajs/react"
import { useEffect, useMemo, useState } from "react"

import { CatalogSearchList } from "@/components/editor/CatalogSearchList"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { Select } from "@/components/ui/select"
import { ADDON_UNITS } from "@/editor-core/addons"
import type { AddOnItem } from "@/editor-core/document"
import type { PageProps } from "@/types/inertia"
import type { TemplateCalcMethod, TemplateCondition, TemplateLine } from "@/types/bids"
import type { CatalogItem } from "@/types/catalog"

const METHOD_OPTIONS: { value: TemplateCalcMethod; label: string }[] = [
  { value: "room_area", label: "Per job area (SF)" },
  { value: "count", label: "Count per job" },
  { value: "manual", label: "Manual quantity" },
]

const CONDITION_OPTIONS: { value: "" | TemplateCondition; label: string }[] = [
  { value: "", label: "No condition" },
  { value: "slab_only", label: "Slab only" },
  { value: "if_stairs", label: "If stairs" },
]

// Input normalizers — the M8 dialog's stance: the dialog keeps values the
// server would 422 out of the payload entirely.
const posNum = (raw: string): number | null => {
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : null
}
const countNum = (raw: string): number | null => {
  const n = Math.round(Number(raw))
  return Number.isFinite(n) ? Math.min(9999, Math.max(0, n)) : null
}
const qtyNum = (raw: string): number | null => {
  const n = Number(raw)
  return Number.isFinite(n) && n >= 0 ? n : null
}

/** Create/edit one order-template line — mirrors the M8 AddOnDialog shape:
 *  catalog-or-manual item, method + rule fields, plus the money additions
 *  (unit cost, export flag). Submits via Inertia (this is a real page, not
 *  the canvas editor). */
export function TemplateLineDialog({
  open,
  onOpenChange,
  addUrl,
  line,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  addUrl: string
  /** Editing an existing line when set; creating otherwise. */
  line: TemplateLine | null
}) {
  const errors = (usePage().props as unknown as PageProps).errors ?? {}

  const [source, setSource] = useState<"catalog" | "manual">("catalog")
  const [picked, setPicked] = useState<CatalogItem | null>(null)
  const [manualName, setManualName] = useState("")
  const [manualUnit, setManualUnit] = useState<string>("EA")
  const [kind, setKind] = useState<"material" | "labor">("material")
  const [description, setDescription] = useState("")
  const [method, setMethod] = useState<TemplateCalcMethod>("room_area")
  const [coverage, setCoverage] = useState("")
  const [minQty, setMinQty] = useState("")
  const [countQty, setCountQty] = useState("")
  const [manualQty, setManualQty] = useState("")
  const [condition, setCondition] = useState<"" | TemplateCondition>("")
  const [unitCost, setUnitCost] = useState("")
  const [freightCost, setFreightCost] = useState("")
  const [exportLine, setExportLine] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  // Seed from the line being edited (or reset for create) each open.
  useEffect(() => {
    if (!open) return
    setSubmitting(false)
    if (line) {
      if (line.item.source === "manual") {
        setSource("manual")
        setPicked(null)
        setManualName(line.item.name)
        setManualUnit(line.item.unit)
      } else {
        setSource("catalog")
        setPicked({
          ...line.item,
          roll_width_ft: null,
          active: true,
          vendor_cost: null,
          freight_cost: null,
        })
        setManualName("")
        setManualUnit("EA")
      }
      setKind(line.kind)
      setDescription(line.description ?? "")
      setMethod(line.calc_method)
      setCoverage(line.coverage_rate?.toString() ?? "")
      setMinQty(line.min_qty?.toString() ?? "")
      setCountQty(line.count_qty?.toString() ?? "")
      setManualQty(line.manual_qty?.toString() ?? "")
      setCondition(line.condition ?? "")
      setUnitCost(line.unit_cost)
      setFreightCost(Number(line.freight_cost) > 0 ? line.freight_cost : "")
      setExportLine(line.include_in_export)
    } else {
      setSource("catalog")
      setPicked(null)
      setManualName("")
      setManualUnit("EA")
      setKind("material")
      setDescription("")
      setMethod("room_area")
      setCoverage("")
      setMinQty("")
      setCountQty("")
      setManualQty("")
      setCondition("")
      setUnitCost("")
      setFreightCost("")
      setExportLine(true)
    }
  }, [open, line])

  const unit = source === "manual" ? manualUnit : (picked?.unit ?? null)
  const needsCoverage =
    method === "room_area" && unit != null && unit !== "SF" && unit !== "SY" && !posNum(coverage)

  const item: AddOnItem | null = useMemo(() => {
    if (source === "manual") {
      return manualName.trim()
        ? { source: "manual", name: manualName.trim().slice(0, 300), unit: manualUnit }
        : null
    }
    if (!picked) return null
    return {
      source: "fc_replica",
      id: picked.id,
      style: picked.style,
      color: picked.color,
      product_type: picked.product_type,
      format_label: picked.format_label,
      unit: picked.unit,
      sqft_per_carton: picked.sqft_per_carton,
      vendor_name: picked.vendor_name,
    }
  }, [source, picked, manualName, manualUnit])

  const canSave = item != null && qtyNum(unitCost) != null && !submitting

  const save = () => {
    if (!item) return
    const payload = {
      kind,
      description: description.trim() || null,
      calc_method: method,
      coverage_rate: method === "room_area" ? posNum(coverage) : null,
      min_qty: method === "room_area" ? posNum(minQty) : null,
      count_qty: method === "count" ? countNum(countQty) : null,
      manual_qty: method === "manual" ? qtyNum(manualQty) : null,
      condition: condition || null,
      unit_cost: qtyNum(unitCost) ?? 0,
      freight_cost: qtyNum(freightCost) ?? 0,
      include_in_export: exportLine,
      item,
    }
    setSubmitting(true)
    const opts = {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
      onFinish: () => setSubmitting(false),
    }
    if (line) {
      router.patch(line.url, { order_template_line: payload }, opts)
    } else {
      router.post(addUrl, { order_template_line: payload }, opts)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" data-testid="template-line-dialog">
        <DialogHeader>
          <DialogTitle>{line ? "Edit line" : "Add line"}</DialogTitle>
          <DialogDescription>
            An item, its quantity rule, and its unit cost — the rule computes against the
            template&apos;s sample lot for the $/SF roll-up.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div data-testid="line-source-toggle">
            <SegmentedControl
              options={[
                { value: "catalog", label: "Catalog" },
                { value: "manual", label: "Typed item" },
              ]}
              value={source}
              onChange={(v) => setSource(v as "catalog" | "manual")}
            />
          </div>

          {source === "catalog" ? (
            <CatalogSearchList
              active={open}
              scope="bid_line"
              picked={picked}
              onPick={(item) => {
                setPicked(item)
                // Pull the catalog's costs in with the pick — editable after,
                // and blank (forcing a conscious entry) when the replica has
                // no cost for the item.
                setUnitCost(item.vendor_cost != null ? String(item.vendor_cost) : "")
                setFreightCost(item.freight_cost != null ? String(item.freight_cost) : "")
              }}
            />
          ) : (
            <div className="flex gap-2">
              <Input
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                placeholder="Item name — e.g. 6 MIL Poly 100 sqft"
                aria-label="Item name"
                className="flex-1"
                data-testid="line-manual-name"
              />
              <Select
                aria-label="Unit"
                value={manualUnit}
                onChange={(e) => setManualUnit(e.target.value)}
                className="w-24"
                data-testid="line-unit-select"
              >
                {ADDON_UNITS.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="line-kind">Line kind</label>
              <Select
                id="line-kind"
                value={kind}
                onChange={(e) => setKind(e.target.value as "material" | "labor")}
                data-testid="line-kind-select"
              >
                <option value="material">Material</option>
                <option value="labor">Labor</option>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="line-description">Color / description</label>
              <Input
                id="line-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="SkimCoat · Painters Tape · matches level color"
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="line-method">Quantity rule</label>
              <Select
                id="line-method"
                value={method}
                onChange={(e) => setMethod(e.target.value as TemplateCalcMethod)}
                data-testid="line-method-select"
              >
                {METHOD_OPTIONS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </div>

            {method === "room_area" && (
              <div className="flex flex-col gap-1">
                <label htmlFor="line-coverage">Coverage — 1 {unit ?? "unit"} per</label>
                <div className="flex items-center gap-2">
                  <Input
                    id="line-coverage"
                    type="number"
                    step="any"
                    value={coverage}
                    onChange={(e) => setCoverage(e.target.value)}
                    placeholder="550"
                    data-testid="line-coverage-rate"
                  />
                  <span className="text-sm text-ink-muted">SF</span>
                </div>
                {needsCoverage && (
                  <p className="text-xs text-signal-display">
                    Required for a {unit} item — an area basis only converts to SF/SY on its own.
                  </p>
                )}
              </div>
            )}
            {method === "room_area" && (
              <div className="flex flex-col gap-1">
                <label htmlFor="line-min">Minimum quantity</label>
                <Input
                  id="line-min"
                  type="number"
                  step="any"
                  value={minQty}
                  onChange={(e) => setMinQty(e.target.value)}
                  placeholder="Optional — e.g. 2"
                  data-testid="line-min-qty"
                />
              </div>
            )}
            {method === "count" && (
              <div className="flex flex-col gap-1">
                <label htmlFor="line-count">Count per job</label>
                <Input
                  id="line-count"
                  type="number"
                  value={countQty}
                  onChange={(e) => setCountQty(e.target.value)}
                  placeholder="Typed — door/stair counts come off the takeoff later"
                  data-testid="line-count"
                />
              </div>
            )}
            {method === "manual" && (
              <div className="flex flex-col gap-1">
                <label htmlFor="line-manual-qty">Quantity</label>
                <Input
                  id="line-manual-qty"
                  type="number"
                  step="any"
                  value={manualQty}
                  onChange={(e) => setManualQty(e.target.value)}
                  data-testid="line-manual-qty"
                />
              </div>
            )}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label htmlFor="line-condition">Condition</label>
              <Select
                id="line-condition"
                value={condition}
                onChange={(e) => setCondition(e.target.value as "" | TemplateCondition)}
                data-testid="line-condition-select"
              >
                {CONDITION_OPTIONS.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-ink-muted">
                Prints on the line — per-lot gating arrives with orders.
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="line-unit-cost">Unit cost</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-ink-muted">$</span>
                <Input
                  id="line-unit-cost"
                  type="number"
                  step="any"
                  value={unitCost}
                  onChange={(e) => setUnitCost(e.target.value)}
                  data-testid="line-unit-cost"
                />
                <span className="text-sm text-ink-muted">per {unit ?? "unit"}</span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="line-freight">Freight</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-ink-muted">$</span>
                <Input
                  id="line-freight"
                  type="number"
                  step="any"
                  value={freightCost}
                  onChange={(e) => setFreightCost(e.target.value)}
                  placeholder="0"
                  data-testid="line-freight-cost"
                />
                <span className="text-sm text-ink-muted">per {unit ?? "unit"}</span>
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 font-normal text-ink-body">
            <Checkbox
              checked={exportLine}
              onChange={(e) => setExportLine(e.target.checked)}
              data-testid="line-export-toggle"
            />
            Include on the ERP order export
          </label>

          {Object.keys(errors).length > 0 && (
            <div className="callout-danger">
              <p>{Object.values(errors).join(" · ")}</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!canSave} data-testid="line-save">
            {line ? "Save line" : "Add line"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
